import {useEffect, useState, useCallback} from 'react';
import {getBasePrice, getFinalPrice, getSpecialPrice} from '../model/price';
import {formatPrice} from '@/lib/use-price';
import {Product} from '@/_application/modules/products/model/product';
import {
    useBuyNowAction,
    generateWebCartItem,
    useAttachedCartItemReferenceLogics,
} from '@kidslabs/module-quote';
import type {BuyNowProductInput} from '@kidslabs/module-quote';

export const useCrossSellProducts = ({mainProduct, data}: {mainProduct: Product; data: Product[]}) => {
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);

    useEffect(() => {
        setSelectedProducts(data);
    }, [data]);

    const {handleBuyNowMultiple, loading: buyNowLoading, errorMessage} = useBuyNowAction();
    const {getAttachedCartItemReferenceLogics, clearAttachedCartItemReferenceLogics} =
        useAttachedCartItemReferenceLogics();

    const finalPriceMain = getFinalPrice(mainProduct);
    const productBasePriceMain = getBasePrice(mainProduct);

    const handleCheckboxChange = useCallback(
        (productId: number) => {
            setSelectedProducts(prevSelected => {
                const index = prevSelected.findIndex(item => item.id === productId);
                if (index !== -1) {
                    return [...prevSelected.slice(0, index), ...prevSelected.slice(index + 1)];
                }
                const productToAdd = data.find(item => item.id === productId);
                if (productToAdd) {
                    return [...prevSelected, productToAdd];
                }
                return prevSelected;
            });
        },
        [data],
    );

    const onSubmit = useCallback(async () => {
        const allProducts = [mainProduct, ...selectedProducts];
        const giftLogics = getAttachedCartItemReferenceLogics();

        const buyNowInputs: BuyNowProductInput[] = allProducts.map((product, index) => {
            const cartItem = generateWebCartItem(product);
            return {
                item: cartItem,
                quantity: 1,
                // Attach gift logics only to the main product (first item)
                cartItemReferenceLogics: index === 0 ? giftLogics : undefined,
                parentProduct: product.__typename === 'ConfigurableProduct' ? product : undefined,
            };
        });

        await handleBuyNowMultiple(buyNowInputs);
        clearAttachedCartItemReferenceLogics();
    }, [
        mainProduct,
        selectedProducts,
        handleBuyNowMultiple,
        getAttachedCartItemReferenceLogics,
        clearAttachedCartItemReferenceLogics,
    ]);

    let totalPrice = finalPriceMain?.value!;
    let totalBasePrice = productBasePriceMain.value!;

    (data ?? []).forEach(item => {
        const finalPrice = getFinalPrice(item);
        const salePrice = getSpecialPrice(item);
        const productBasePrice = getBasePrice(item);
        const calculatedPrice = salePrice > 0 ? salePrice : finalPrice?.value!;

        if (selectedProducts.some(selectedItem => selectedItem.id === item.id)) {
            totalPrice += calculatedPrice;
            totalBasePrice += productBasePrice.value!;
        }
    });

    const totalSavings = formatPrice({amount: totalBasePrice - totalPrice, currencyCode: 'VND', locale: 'vi'});

    return {
        totalSavings,
        selectedProducts,
        totalPrice,
        totalBasePrice,
        onSubmit,
        handleCheckboxChange,
        isAddToCartLoading: buyNowLoading,
        errorMessage,
    };
};
